import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Button } from '@/components/ui/Button/Button';
import { FilterPopoverShell } from '@/components/ProjectsTable/filter-popover/FilterPopoverShell';
import { EdgeToEdgeTabs } from '@/components/ui/EdgeToEdgeTabs/EdgeToEdgeTabs';
import { CloseIcon } from '@/components/ui/Icons';
import { FilterOptionsChecklist } from '@/components/ProjectsTable/FilterPopover';
import type { FilterRule } from '@/lib/tableFilters/filterRuleTypes';
import { groupReaderTagsByGroup } from '@/lib/readerTagCatalogGrouping';
import { isRuleEmpty } from '@/lib/tableFilters/filterRuleEmpty';
import '@/components/ProjectsTable/EditableCell.css';
import '@/components/ProjectsTable/FilterPopover.css';

type ReaderTagsFacetRule = Extract<FilterRule, { type: 'readerTagsFacet' }>;

function facetsFromRule(rule: FilterRule | null): Record<string, string[]> {
  if (rule?.type !== 'readerTagsFacet') return {};
  const out: Record<string, string[]> = {};
  for (const [g, ids] of Object.entries(rule.facets ?? {})) {
    if (!ids?.length) continue;
    out[g] = [...ids];
  }
  return out;
}

interface ReaderTagsFacetFilterPopoverProps {
  currentRule: FilterRule | null;
  onApply: (rule: FilterRule | null) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
}

export function ReaderTagsFacetFilterPopover({
  currentRule,
  onApply,
  triggerRef,
  isOpen,
  onClose,
}: ReaderTagsFacetFilterPopoverProps) {
  const tagsQuery = useQuery({
    queryKey: ['readerTags'],
    queryFn: () => api.readerTags.list(),
  });

  const grouped = useMemo(() => groupReaderTagsByGroup(tagsQuery.data ?? []), [tagsQuery.data]);

  const [activeTab, setActiveTab] = useState('');
  const [draftByGroup, setDraftByGroup] = useState<Record<string, string[]>>({});

  const tabItems = useMemo(
    () =>
      grouped.map((g) => {
        const groupHasSelections = (draftByGroup[g.group]?.length ?? 0) > 0;
        const showTabDot = groupHasSelections;
        return {
          id: g.group,
          label: g.group,
          adornment: showTabDot ? (
            <span className="filter-popover__combo-tab-dot" aria-hidden />
          ) : undefined,
          adornmentPlacement: 'start' as const,
        };
      }),
    [grouped, draftByGroup]
  );

  useEffect(() => {
    if (isOpen) {
      setDraftByGroup(facetsFromRule(currentRule));
      const first = grouped[0]?.group ?? '';
      setActiveTab(first);
    }
  }, [isOpen, currentRule, grouped]);

  useEffect(() => {
    if (activeTab === '' && tabItems.length > 0) {
      setActiveTab(tabItems[0]!.id);
    }
  }, [activeTab, tabItems]);

  const labelsByTagId = useMemo(() => {
    const catalog = tagsQuery.data ?? [];
    const m: Record<string, string> = {};
    for (const t of catalog) {
      m[t.id] = t.name;
    }
    return m;
  }, [tagsQuery.data]);

  const activeBucket = grouped.find((g) => g.group === activeTab);
  const optionIds = useMemo(() => activeBucket?.tags.map((t) => t.id) ?? [], [activeBucket]);

  const handleClearAll = useCallback(() => {
    setDraftByGroup({});
  }, []);

  const handleApplyClose = useCallback(
    (requestClose: () => void) => {
      const facets: Record<string, string[]> = {};
      for (const [g, ids] of Object.entries(draftByGroup)) {
        if (ids?.length) facets[g] = [...ids];
      }
      const next: ReaderTagsFacetRule = { type: 'readerTagsFacet', facets };
      onApply(isRuleEmpty(next) ? null : next);
      requestClose();
    },
    [draftByGroup, onApply]
  );

  const selectedForTab = draftByGroup[activeTab] ?? [];

  const setSelectedForTab = useCallback(
    (nextIds: string[]) => {
      setDraftByGroup((prev) => {
        const merged = { ...prev };
        if (nextIds.length === 0) delete merged[activeTab];
        else merged[activeTab] = nextIds;
        return merged;
      });
    },
    [activeTab]
  );

  return (
    <FilterPopoverShell
      isOpen={isOpen}
      onClose={onClose}
      triggerRef={triggerRef}
      positionDependencies={['reader-tags-facet']}
      panelWidthPx={380}
    >
      {({ requestClose }) => (
        <>
          <div className="filter-popover__header">
            <span className="filter-popover__title">
              <span className="filter-popover__title-prefix">Фильтр:</span>
              Теги
            </span>
            <button type="button" className="filter-popover__close-btn" onClick={requestClose} aria-label="Закрыть">
              <CloseIcon size={16} />
            </button>
          </div>

          {!tagsQuery.data?.length ? (
            <div className="filter-popover__body">
              <div className="filter-popover__scroll">
                <div className="filter-popover__section-body">
                  {tagsQuery.isFetching ? (
                    <p className="filter-popover__empty">Загрузка справочника тегов…</p>
                  ) : (
                    <p className="filter-popover__empty">Нет тегов в справочнике</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="filter-popover__body">
              {tabItems.length > 1 ? (
                <div className="filter-popover__tabs">
                  <EdgeToEdgeTabs items={tabItems} value={activeTab} onChange={setActiveTab} aria-label="Группы тегов" />
                </div>
              ) : null}
              <div className="filter-popover__scroll">
                <div className="filter-popover__section-body">
                  <FilterOptionsChecklist
                    options={optionIds}
                    selected={selectedForTab}
                    onChange={setSelectedForTab}
                    searchTextOf={(id) => labelsByTagId[id] ?? id}
                    renderOptionLabel={(id) => labelsByTagId[id] ?? id}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="filter-popover__footer editable-cell-popover__footer">
            <Button variant="secondary" size="s" fullWidth onClick={handleClearAll}>
              Очистить всё
            </Button>
            <Button variant="primary" size="s" fullWidth onClick={() => handleApplyClose(requestClose)}>
              Применить
            </Button>
          </div>
        </>
      )}
    </FilterPopoverShell>
  );
}
